/**
 * useStatementGapDetection — detects gaps in financial statement coverage.
 *
 * Fetches account_statement_periods for the current user, groups them by
 * account (institution + type + last4), and identifies months with no
 * statement coverage. This is critical for certificate-of-compliance
 * validation in family law proceedings.
 *
 * Tables used:
 *   - account_statement_periods
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAdvisorSafe } from '@/contexts/AdvisorContext';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';
import { buildCategoryRangesFromTasks } from '@/lib/disclosure/taskRequirementOverrides';
import type { DisclosureCategory } from '@/lib/disclosure/calculateProgress';

/**
 * Maps account_type values from account_statement_periods to disclosure
 * categories. Lives here (rather than useDisclosureCoverage, which re-exports
 * it) so attorney-assigned category windows can be matched to accounts
 * without a circular import (#1314).
 */
export const ACCOUNT_TYPE_TO_CATEGORY: Record<string, DisclosureCategory> = {
  // Bank accounts
  checking: 'bank_accounts',
  savings: 'bank_accounts',
  money_market: 'bank_accounts',
  cd: 'bank_accounts',
  depository: 'bank_accounts',
  // Credit cards
  credit_card: 'credit_cards',
  credit: 'credit_cards',
  // Investments
  brokerage: 'investments',
  investment: 'investments',
  stocks: 'investments',
  // Retirement
  '401k': 'retirement',
  '403b': 'retirement',
  ira: 'retirement',
  roth_ira: 'retirement',
  pension: 'retirement',
  retirement: 'retirement',
  // Mortgages
  mortgage: 'mortgages',
  heloc: 'mortgages',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StatementPeriod {
  id: string;
  user_id: string;
  account_entity_id: string | null;
  document_id: string;
  institution_name: string | null;
  account_type: string | null;
  account_last4: string | null;
  period_start: string; // ISO date
  period_end: string;   // ISO date
  opening_balance: number | null;
  closing_balance: number | null;
  created_at: string;
}

export interface DateRange {
  start: Date;
  end: Date;
}

export interface PeriodWithDoc {
  start: Date;
  end: Date;
  documentId: string;
}

export interface AccountCoverage {
  accountKey: string;       // e.g. "Chase Checking (4521)"
  institution: string;
  accountType: string;
  accountLast4: string;
  periods: PeriodWithDoc[];
  gaps: DateRange[];
  totalExpectedMonths: number;
  coveredMonths: number;
  coveragePercentage: number;
}

// ---------------------------------------------------------------------------
// Pure logic (exported for testing)
// ---------------------------------------------------------------------------

/** Build a human-readable account key like "Chase Checking (4521)". */
export function buildAccountKey(
  institution: string | null,
  accountType: string | null,
  last4: string | null,
): string {
  const parts: string[] = [];
  if (institution) parts.push(institution);
  if (accountType) parts.push(accountType);
  const base = parts.length > 0 ? parts.join(' ') : 'Unknown Account';
  return last4 ? `${base} (${last4})` : base;
}

/**
 * Given a list of sorted, non-overlapping periods, detect month-level gaps.
 *
 * Expected range: earliest period_start -> latest period_end.
 * A "month" is represented by (year, month). A month is covered if any
 * period intersects that month.
 */
export function detectGaps(periods: PeriodWithDoc[]): {
  gaps: DateRange[];
  totalExpectedMonths: number;
  coveredMonths: number;
} {
  if (periods.length === 0) {
    return { gaps: [], totalExpectedMonths: 0, coveredMonths: 0 };
  }

  // Sort by start date
  const sorted = [...periods].sort(
    (a, b) => a.start.getTime() - b.start.getTime(),
  );

  const rangeStart = sorted[0].start;
  const rangeEnd = sorted.reduce(
    (latest, p) => (p.end > latest ? p.end : latest),
    sorted[0].end,
  );

  // Walk month by month from rangeStart to rangeEnd
  const coveredSet = new Set<string>(); // "YYYY-MM"
  const allMonths: string[] = [];

  let cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
  const endMonth = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), 1);

  while (cursor <= endMonth) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    allMonths.push(key);
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }

  // Mark covered months
  for (const period of sorted) {
    let m = new Date(period.start.getFullYear(), period.start.getMonth(), 1);
    const pEnd = new Date(period.end.getFullYear(), period.end.getMonth(), 1);
    while (m <= pEnd) {
      const key = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
      coveredSet.add(key);
      m = new Date(m.getFullYear(), m.getMonth() + 1, 1);
    }
  }

  // Build gap ranges from uncovered months
  const gaps: DateRange[] = [];
  let gapStart: Date | null = null;

  for (const monthKey of allMonths) {
    const [y, mo] = monthKey.split('-').map(Number);
    if (!coveredSet.has(monthKey)) {
      if (!gapStart) {
        gapStart = new Date(y, mo - 1, 1);
      }
    } else {
      if (gapStart) {
        // End of gap: last day of previous month
        const prevIdx = allMonths.indexOf(monthKey) - 1;
        const [py, pm] = allMonths[prevIdx].split('-').map(Number);
        const gapEnd = new Date(py, pm, 0); // last day of that month
        gaps.push({ start: gapStart, end: gapEnd });
        gapStart = null;
      }
    }
  }

  // Close any trailing gap
  if (gapStart) {
    const lastKey = allMonths[allMonths.length - 1];
    const [ly, lm] = lastKey.split('-').map(Number);
    const gapEnd = new Date(ly, lm, 0);
    gaps.push({ start: gapStart, end: gapEnd });
  }

  return {
    gaps,
    totalExpectedMonths: allMonths.length,
    coveredMonths: coveredSet.size,
  };
}

/**
 * Represents a custom required date range for a specific account,
 * typically sourced from a document_task record.
 */
export interface CustomAccountRange {
  /** Last 4 digits of the account to match */
  accountLast4: string;
  /** Document category (e.g., 'financial.bank_statement') */
  documentType?: string;
  /** Required range start */
  start: Date;
  /** Required range end */
  end: Date;
}

/**
 * Given sorted periods and a required range, detect month-level gaps
 * within that range. Months outside the required range are ignored.
 */
export function detectGapsInRange(
  periods: PeriodWithDoc[],
  requiredRange: DateRange,
): {
  gaps: DateRange[];
  totalExpectedMonths: number;
  coveredMonths: number;
} {
  const rangeStart = requiredRange.start;
  const rangeEnd = requiredRange.end;

  // Build all months in the required range
  const coveredSet = new Set<string>();
  const allMonths: string[] = [];

  let cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
  const endMonth = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), 1);

  while (cursor <= endMonth) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    allMonths.push(key);
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }

  if (allMonths.length === 0) {
    return { gaps: [], totalExpectedMonths: 0, coveredMonths: 0 };
  }

  // Mark covered months from periods
  for (const period of periods) {
    let m = new Date(period.start.getFullYear(), period.start.getMonth(), 1);
    const pEnd = new Date(period.end.getFullYear(), period.end.getMonth(), 1);
    while (m <= pEnd) {
      const key = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
      if (allMonths.includes(key)) {
        coveredSet.add(key);
      }
      m = new Date(m.getFullYear(), m.getMonth() + 1, 1);
    }
  }

  // Build gap ranges
  const gaps: DateRange[] = [];
  let gapStart: Date | null = null;

  for (const monthKey of allMonths) {
    const [y, mo] = monthKey.split('-').map(Number);
    if (!coveredSet.has(monthKey)) {
      if (!gapStart) {
        gapStart = new Date(y, mo - 1, 1);
      }
    } else {
      if (gapStart) {
        const prevIdx = allMonths.indexOf(monthKey) - 1;
        const [py, pm] = allMonths[prevIdx].split('-').map(Number);
        const gapEnd = new Date(py, pm, 0);
        gaps.push({ start: gapStart, end: gapEnd });
        gapStart = null;
      }
    }
  }

  if (gapStart) {
    const lastKey = allMonths[allMonths.length - 1];
    const [ly, lm] = lastKey.split('-').map(Number);
    const gapEnd = new Date(ly, lm, 0);
    gaps.push({ start: gapStart, end: gapEnd });
  }

  return {
    gaps,
    totalExpectedMonths: allMonths.length,
    coveredMonths: coveredSet.size,
  };
}

/**
 * Category-level custom range (e.g. an attorney-assigned task window that
 * applies to every account in a disclosure category). Matched via
 * ACCOUNT_TYPE_TO_CATEGORY when no account-specific range applies.
 */
export interface CustomCategoryRange {
  category: string;
  start: Date;
  end: Date;
}

/**
 * Group raw statement periods by account and compute coverage + gaps.
 *
 * If customRanges are provided, accounts matching by last4 (and optionally
 * documentType) will use the custom range instead of the default behavior
 * of using the earliest-to-latest period range.
 *
 * If categoryRanges are provided (#1314), accounts WITHOUT an account-specific
 * custom range are measured against the attorney-assigned window for their
 * disclosure category (matched via ACCOUNT_TYPE_TO_CATEGORY). Account-specific
 * ranges always take precedence over category windows.
 */
export function computeAccountCoverages(
  rows: StatementPeriod[],
  customRanges?: CustomAccountRange[],
  categoryRanges?: CustomCategoryRange[],
): AccountCoverage[] {
  // Group by last4 only — institution names vary across AI extractions
  // (e.g. "Chase Bank" vs "Chase" vs "JPMorgan Chase Bank, N.A.")
  const groups = new Map<string, {
    institutions: string[];
    accountTypes: string[];
    accountLast4: string;
    periods: PeriodWithDoc[];
  }>();

  for (const row of rows) {
    const inst = row.institution_name ?? '';
    const type = row.account_type ?? '';
    const last4 = row.account_last4 ?? '';
    // Use last4 as the primary key; fall back to full composite only if no last4
    const key = last4 || `${inst}||${type}`;

    if (!groups.has(key)) {
      groups.set(key, {
        institutions: [],
        accountTypes: [],
        accountLast4: last4,
        periods: [],
      });
    }

    const group = groups.get(key)!;
    if (inst) group.institutions.push(inst);
    if (type) group.accountTypes.push(type);

    group.periods.push({
      start: new Date(row.period_start + 'T00:00:00'),
      end: new Date(row.period_end + 'T00:00:00'),
      documentId: row.document_id,
    });
  }

  const results: AccountCoverage[] = [];

  // Pick the most frequent value from an array of strings
  function mostCommon(values: string[]): string {
    if (values.length === 0) return '';
    const counts = new Map<string, number>();
    for (const v of values) {
      counts.set(v, (counts.get(v) || 0) + 1);
    }
    let best = '';
    let bestCount = 0;
    for (const [v, c] of counts) {
      if (c > bestCount) { best = v; bestCount = c; }
    }
    return best;
  }

  for (const [, group] of groups) {
    const institution = mostCommon(group.institutions);
    const accountType = mostCommon(group.accountTypes);
    const accountKey = buildAccountKey(
      institution || null,
      accountType || null,
      group.accountLast4 || null,
    );

    // Check for custom range matching by last4 (account-specific wins)
    const customRange = customRanges?.find(
      (cr) => cr.accountLast4 === group.accountLast4,
    );

    // Otherwise, check for an attorney-assigned category window (#1314)
    const mappedCategory = ACCOUNT_TYPE_TO_CATEGORY[accountType.toLowerCase()];
    const categoryRange = !customRange && mappedCategory
      ? categoryRanges?.find((cr) => cr.category === mappedCategory)
      : undefined;

    let gapResult: { gaps: DateRange[]; totalExpectedMonths: number; coveredMonths: number };

    if (customRange) {
      gapResult = detectGapsInRange(group.periods, {
        start: customRange.start,
        end: customRange.end,
      });
    } else if (categoryRange) {
      gapResult = detectGapsInRange(group.periods, {
        start: categoryRange.start,
        end: categoryRange.end,
      });
    } else {
      gapResult = detectGaps(group.periods);
    }

    results.push({
      accountKey,
      institution,
      accountType,
      accountLast4: group.accountLast4,
      periods: group.periods,
      gaps: gapResult.gaps,
      totalExpectedMonths: gapResult.totalExpectedMonths,
      coveredMonths: gapResult.coveredMonths,
      coveragePercentage:
        gapResult.totalExpectedMonths > 0
          ? Math.round((gapResult.coveredMonths / gapResult.totalExpectedMonths) * 100)
          : 100,
    });
  }

  // Sort accounts alphabetically
  results.sort((a, b) => a.accountKey.localeCompare(b.accountKey));
  return results;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

function useUserId() {
  const { user } = useAuth();
  const { selectedClientId, isAdvisorMode } = useAdvisorSafe();
  return isAdvisorMode ? selectedClientId : user?.id;
}

/**
 * Build CustomAccountRange[] from document_tasks rows.
 * Matches tasks that have an account_identifier (last4) and date range.
 */
export function buildCustomRangesFromTasks(
  tasks: Array<{
    account_identifier: string | null;
    document_category_id: string | null;
    date_range_start: string | null;
    date_range_end: string | null;
  }>,
): CustomAccountRange[] {
  const ranges: CustomAccountRange[] = [];

  for (const task of tasks) {
    if (!task.account_identifier || !task.date_range_start || !task.date_range_end) {
      continue;
    }
    ranges.push({
      accountLast4: task.account_identifier,
      documentType: task.document_category_id ?? undefined,
      start: new Date(task.date_range_start + 'T00:00:00'),
      end: new Date(task.date_range_end + 'T00:00:00'),
    });
  }

  return ranges;
}

export function useStatementGapDetection(): {
  accounts: AccountCoverage[];
  totalGaps: number;
  isLoading: boolean;
} {
  const userId = useUserId();

  const { data: rows = [], isLoading: periodsLoading } = useQuery({
    queryKey: ['statement-periods', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_statement_periods')
        .select('*')
        .eq('user_id', userId!);

      if (error) {
        trackSupabaseError('select', 'account_statement_periods', error);
        throw error;
      }
      return data as StatementPeriod[];
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Fetch document_tasks to build custom date ranges (per-account AND
  // category-level attorney-assigned windows, #1314)
  const { data: taskRows = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['document-tasks-ranges', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('document_tasks')
        .select('id, status, account_identifier, document_category_id, date_range_start, date_range_end')
        .eq('user_id', userId!)
        .not('date_range_start', 'is', null)
        .not('date_range_end', 'is', null);

      if (error) {
        trackSupabaseError('select', 'document_tasks', error);
        throw error;
      }
      return data as Array<{
        id: string;
        status: string;
        account_identifier: string | null;
        document_category_id: string | null;
        date_range_start: string | null;
        date_range_end: string | null;
      }>;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 2,
  });

  const customRanges = useMemo(
    () => buildCustomRangesFromTasks(taskRows),
    [taskRows],
  );

  const categoryRanges = useMemo(
    () => buildCategoryRangesFromTasks(taskRows),
    [taskRows],
  );

  const accounts = useMemo(
    () =>
      computeAccountCoverages(
        rows,
        customRanges.length > 0 ? customRanges : undefined,
        categoryRanges.length > 0 ? categoryRanges : undefined,
      ),
    [rows, customRanges, categoryRanges],
  );

  const totalGaps = useMemo(
    () => accounts.reduce((sum, a) => sum + a.gaps.length, 0),
    [accounts],
  );

  const isLoading = periodsLoading || tasksLoading;

  return { accounts, totalGaps, isLoading };
}
